// MESHES ASKED FOR BEFORE THE SCREEN THAT DRAWS THEM — the public tutorial's, where a screen is
// two glbs of up to 28 MB and the visitor's connection is the whole of the wait: the server
// hands one over in 30 ms and it takes ten to thirty-five seconds to arrive.
//
// THE BYTES GO TO THREE'S OWN CACHE, which is where the scene's loader finds them a moment
// later (see loadDeps, which turns it on for this page). They went to the browser's HTTP cache
// alone, and a private window on an iPhone did not answer the loader from it: every mesh came
// down twice. Packed, a screen's meshes are tens of KB, so holding them costs little.

// Every address asked for so far, with the download it started. A failed one is forgotten, so
// the scene that needs it asks again itself.
const asked = new Map<string, Promise<void>>();

const ask = (url: string): Promise<void> => {
  const had = asked.get(url);
  if (had) return had;
  const arriving = fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      // Read to the end: a response nobody reads is one the cache never finishes writing.
      return r.arrayBuffer();
    })
    // As three's loader would have stored it, under the same address (a no-op where its cache
    // is off).
    .then(async (bytes) => { (await import('three')).Cache.add(`file:${url}`, bytes); }, () => { asked.delete(url); });
  asked.set(url, arriving);
  return arriving;
};

// Which call to `preloadMeshes` is the current one — see below.
let turn = 0;

// Download these, ONE SCREEN AT A TIME and in the order given: `screens` is a list of screens,
// each the addresses it draws. A screen's own meshes go together, as its scene asks for them;
// the screens do not, because they would share one connection and the screen being looked at
// would wait on the ones that are not.
//
// EACH CALL REPLACES THE LAST. The visitor has moved, so what comes next has changed — but a
// download already under way is left to finish: dropping it throws away bytes that screen will
// want in a minute.
export function preloadMeshes(screens: string[][]): void {
  const mine = ++turn;
  void (async () => {
    for (const urls of screens) {
      if (mine !== turn) return;
      await Promise.all(urls.map(ask));
    }
  })();
}

// The download of `url` already under way, if there is one — what a scene waits on INSTEAD of
// starting its own, which would be the same megabytes twice down the same connection. Nothing
// to wait for anywhere this file was never called, which is the whole app bar the public page.
export const meshArriving = (url: string): Promise<void> | undefined => asked.get(url);
